export const cleanAndParseSummary = (summary) => {
    if (!summary) return null;
    if (typeof summary === 'object') return summary;
    let cleaned = summary.trim();
    cleaned = cleaned.replace(/^```json/, '').replace(/```$/, '').trim();
    try {
        return JSON.parse(cleaned);
    } catch (e) {
        try { return JSON.parse(summary); } catch { return null; }
    }
};

export const fuzzyMatchLocation = (geminiCity, locations) => {
    if (!geminiCity || !locations || !Array.isArray(locations) || locations.length === 0) {
        return null;
    }
    const normalizeString = (str) => {
        if (!str || typeof str !== 'string') return '';
        return str.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
    };

    const superNormalize = (str) => {
        if (!str || typeof str !== 'string') return '';
        return normalizeString(str)
            .replace(/\b(city|town|district|state|province|india|airport|station|terminal|hotel|resort|stay)\b/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    };

    const normalizedGeminiCity = normalizeString(geminiCity);
    const sGemini = superNormalize(geminiCity);
    if (!normalizedGeminiCity) return null;

    // 0. Strict Case-Insensitive Match (Pre-normalization)
    const exactMatch = locations.find(loc => {
        const dbName = loc?.name?.toLowerCase().trim();
        const gName = geminiCity.toLowerCase().trim();
        return dbName === gName || dbName.includes(gName) || gName.includes(dbName);
    });
    if (exactMatch) return exactMatch;

    // 1. Direct/Prefix/Sub-string match
    for (const location of locations) {
        const locationName = location?.name;
        if (!locationName) continue;
        const normalizedDbName = normalizeString(locationName);

        if (normalizedDbName === normalizedGeminiCity) return location;

        // Handle partial matches like "Tezp" for "Tezpur" (minimum 3 chars to avoid false positives)
        if (normalizedGeminiCity.length >= 3) {
            if (normalizedDbName.startsWith(normalizedGeminiCity) || normalizedGeminiCity.startsWith(normalizedDbName)) return location;
        }

        if (normalizedDbName.includes(normalizedGeminiCity) || normalizedGeminiCity.includes(normalizedDbName)) return location;
    }

    // 2. Super Normalized Match (Removing "City", "Hotel" etc)
    if (sGemini.length >= 3) {
        for (const location of locations) {
            const sDb = superNormalize(location?.name);
            if (sDb && sGemini && (sDb.includes(sGemini) || sGemini.includes(sDb))) return location;
        }
    }

    // 3. Levenshtein Distance match
    const levenshteinDistance = (a, b) => {
        if (!a || !b) return Infinity;
        const dp = Array(a.length + 1).fill().map(() => Array(b.length + 1).fill(0));
        for (let i = 0; i <= a.length; i++) dp[i][0] = i;
        for (let j = 0; j <= b.length; j++) dp[0][j] = j;
        for (let i = 1; i <= a.length; i++) {
            for (let j = 1; j <= b.length; j++) {
                if (a[i - 1] === b[j - 1]) dp[i][j] = dp[i - 1][j - 1];
                else dp[i][j] = Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]) + 1;
            }
        }
        return dp[a.length][b.length];
    };

    let bestMatch = null;
    let bestScore = Infinity;
    const threshold = Math.min(3, Math.floor(normalizedGeminiCity.length * 0.3));
    for (const location of locations) {
        const locationName = location?.name;
        if (locationName) {
            const nameDistance = levenshteinDistance(normalizedGeminiCity, normalizeString(locationName));
            if (nameDistance < bestScore && nameDistance <= threshold) {
                bestScore = nameDistance;
                bestMatch = location;
            }
        }
    }
    return bestMatch;
};

// Helper to standardize date strings to YYYY-MM-DD for <input type="date">
// Prevents -1 day issues by avoiding UTC conversion shifts
export const standardizeDate = (dateStr) => {
    const val = normalizeValue(dateStr);
    if (!val) return "";

    // If it's already YYYY-MM-DD and within allowed year range
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
        const yr = parseInt(val.slice(0, 4));
        if (yr < 2000 || yr > 2100) return "";
        return val;
    }

    // Strip ordinal suffixes like 1st, 2nd, 3rd, 4th, 5th, 15th...
    const cleanOrdinal = val.replace(/(\d+)(st|nd|rd|th)/gi, '$1');
    let cleanVal = cleanOrdinal.replace(/[.,/]/g, '-').replace(/\s+/g, '-');

    // Attempt manual parse for DD-MM-YYYY or DD-MMM-YYYY
    const months = {
        jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
        jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
        sept: '09', september: '09', october: '10', november: '11', december: '12',
        january: '01', february: '02', march: '03', april: '04', june: '06', july: '07', august: '08'
    };

    const parts = cleanVal.split('-').filter(Boolean);
    if (parts.length === 3) {
        let day, month, year;

        // Pattern 1: DD-MM-YYYY or DD-MMM-YYYY
        if (parts[2].length === 4) {
            day = parts[0].replace(/\D/g, '').padStart(2, '0');
            year = parts[2];
            const mKey = parts[1].toLowerCase().substring(0, 3);
            month = months[mKey] || parts[1].replace(/\D/g, '').padStart(2, '0');
            // Swap if appears to be MM-DD-YYYY
            if (parseInt(month) > 12 && parseInt(day) <= 12) {
                [day, month] = [month, day];
            }
            const yrNum = parseInt(year);
            if (yrNum < 2000 || yrNum > 2100) return "";
            if (parseInt(month) <= 12 && parseInt(day) <= 31) {
                return `${year}-${month}-${day}`;
            }
        }
        // Pattern 2: YYYY-MM-DD (fallback)
        if (parts[0].length === 4) {
            const yrNum = parseInt(parts[0]);
            if (yrNum < 2000 || yrNum > 2100) return "";
            const year = parts[0];
            const mKey = parts[1].toLowerCase().substring(0, 3);
            const month = months[mKey] || parts[1].replace(/\D/g, '').padStart(2, '0');
            const day = parts[2].replace(/\D/g, '').padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
    }

    // Final fallback using Date constructor
    const date = new Date(cleanOrdinal);
    if (!isNaN(date.getTime())) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        if (y < 2000 || y > 2100) return "";
        return `${y}-${m}-${d}`;
    }

    return val;
};

// Helper to format date for display (DD MMM YYYY)
export const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    // Remove ordinal suffixes to help parsing
    const cleanDate = String(dateStr).replace(/(\d+)(st|nd|rd|th)/g, "$1");
    const date = new Date(cleanDate);
    if (isNaN(date.getTime())) return dateStr;

    const d = String(date.getDate()).padStart(2, '0');
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const m = months[date.getMonth()];
    const y = date.getFullYear();
    return `${d} ${m} ${y}`;
};

// Helper to standardize time strings to HH:mm for <input type="time">
export const standardizeTime = (timeStr) => {
    const val = normalizeValue(timeStr);
    if (!val) return "";

    // Clean string from minor noise
    let cleanVal = val.replace(/[.]/g, ':').replace(/\s+/g, ' ').toUpperCase();

    // 1. Try to parse HH:mm AM/PM
    const ampmMatch = cleanVal.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/);
    if (ampmMatch) {
        let hours = parseInt(ampmMatch[1]);
        const minutes = ampmMatch[2];
        const modifier = ampmMatch[3];
        if (modifier === 'PM' && hours < 12) hours += 12;
        if (modifier === 'AM' && hours === 12) hours = 0;
        return `${String(hours).padStart(2, '0')}:${minutes}`;
    }

    // 2. Try to parse HH mm AM/PM (space instead of colon)
    const ampmMatchSpace = cleanVal.match(/(\d{1,2})\s+(\d{2})\s*(AM|PM)/);
    if (ampmMatchSpace) {
        let hours = parseInt(ampmMatchSpace[1]);
        const minutes = ampmMatchSpace[2];
        const modifier = ampmMatchSpace[3];
        if (modifier === 'PM' && hours < 12) hours += 12;
        if (modifier === 'AM' && hours === 12) hours = 0;
        return `${String(hours).padStart(2, '0')}:${minutes}`;
    }

    // 3. Try to parse HH AM/PM (no minutes)
    const ampmMatchNoMin = cleanVal.match(/(\d{1,2})\s*(AM|PM)/);
    if (ampmMatchNoMin) {
        let hours = parseInt(ampmMatchNoMin[1]);
        const modifier = ampmMatchNoMin[2];
        if (modifier === 'PM' && hours < 12) hours += 12;
        if (modifier === 'AM' && hours === 12) hours = 0;
        return `${String(hours).padStart(2, '0')}:00`;
    }

    // 4. Fallback for pure HH:mm
    const simpleMatch = cleanVal.match(/(\d{1,2}):(\d{2})/);
    if (simpleMatch) {
        return `${simpleMatch[1].padStart(2, '0')}:${simpleMatch[2]}`;
    }

    return "";
};

// Helper to normalize strings and filter out common "empty" placeholders
export const normalizeValue = (val) => {
    if (val === null || val === undefined) return null;
    let str = String(val).trim();
    if (!str) return null;

    const placeholders = [
        '--', '--:--', '--:-- --', '-- : -- --',
        'dd/mm/yy', 'dd-mm-yy', 'dd/mm/yyyy',
        'null', 'undefined', 'n/a', 'pending', 'tbd'
    ];
    if (placeholders.includes(str.toLowerCase())) return null;
    return str;
};

export const sanitizeTerminalNumber = (val) => {
    if (!val || typeof val !== 'string') return '';
    const trimmed = val.trim();
    if (!trimmed) return '';

    const lower = trimmed.toLowerCase();
    if (lower.includes('airport') || lower.includes('national') || lower.includes('international') || lower.includes('aerodrome') || lower.includes('domestic')) {
        const match = trimmed.match(/\b(?:terminal|t)[-\s]*([0-9]+[a-z]?)\b/i) || trimmed.match(/\bT([0-9]+)\b/i);
        if (match) {
            return match[1].toUpperCase();
        }
        return '';
    }

    const matchNum = trimmed.match(/^(?:terminal|t|pf|platform)?[-\s]*([0-9]+[a-z]?)$/i);
    if (matchNum) {
        return matchNum[1].toUpperCase();
    }

    const genericMatch = trimmed.match(/\b(?:terminal|t)[-\s]*([0-9]+[a-z]?)\b/i);
    if (genericMatch) {
        return genericMatch[1].toUpperCase();
    }

    const digitMatch = trimmed.match(/\b([0-9]+[a-z]?)\b/i);
    if (digitMatch && trimmed.length <= 6) {
        return digitMatch[1].toUpperCase();
    }

    return '';
};

export const postProcessAiData = (summaryData, allLocations) => {
    if (!summaryData) return summaryData;
    let processed = { ...summaryData };

    // 0. Guest Information Mapping & Fallbacks
    if (processed.guest_information) {
        const info = processed.guest_information;
        info.guest_name = normalizeValue(info.guest_name);
        info.contact_number = normalizeValue(info.contact_number);
        // Map total_rooms if not explicitly there but exists at top level or guest level
        if (!info.total_rooms_booked && processed.total_rooms_booked) {
            info.total_rooms_booked = processed.total_rooms_booked;
        }
    }

    // 1. Hotel Bookings Mapping & Normalization
    if (processed.hotel_bookings && Array.isArray(processed.hotel_bookings)) {
        processed.hotel_bookings = processed.hotel_bookings.map(hotel => {
            // Map alternative fields from AI response
            const checkInTimeVal = standardizeTime(hotel.check_in_time || hotel.checkin_time || hotel.checkInTime || hotel.time_check_in || hotel.check_in_timing);
            const checkOutTimeVal = standardizeTime(hotel.check_out_time || hotel.checkout_time || hotel.checkOutTime || hotel.time_check_out || hotel.check_out_timing);
            const mappedCheckInDate = standardizeDate(hotel.check_in_date || hotel.check_in || hotel.arrival_date || hotel.checkIn || hotel.checkin);
            const mappedCheckOutDate = standardizeDate(hotel.check_out_date || hotel.check_out || hotel.departure_date || hotel.checkOut || hotel.checkout);

            const mappedHotel = {
                ...hotel,
                hotel_name: normalizeValue(hotel.hotel_name || hotel.name || hotel.hotelName),
                hotel_address: normalizeValue(hotel.hotel_address || hotel.address),
                hotel_phone: normalizeValue(hotel.hotel_phone || hotel.phone || hotel.hotel_contact || hotel.contact_number || hotel.phone_number || hotel.tel || hotel.telephone || hotel.contact),
                map_link: normalizeValue(hotel.map_link || hotel.hotel_map_link || hotel.google_map_link || hotel.maps_link || hotel.map_url),
                confirmation_number: normalizeValue(hotel.confirmation_number || hotel.booking_reference || hotel.confirmation_no || hotel.voucher_no || hotel.bookingRef || hotel.confirmationNumber),
                check_in_date: mappedCheckInDate,
                check_in_time: checkInTimeVal || (mappedCheckInDate ? '14:00' : ''),
                check_out_date: mappedCheckOutDate,
                check_out_time: checkOutTimeVal || (mappedCheckOutDate ? '11:00' : ''),
                city: normalizeValue(hotel.city || hotel.location || hotel.place || hotel.dest),
                no_of_rooms: hotel.no_of_rooms || hotel.rooms_count || hotel.rooms || processed.guest_information?.total_rooms_booked || null,
                no_of_nights: hotel.no_of_nights || hotel.nights || hotel.night_count || null,
                room_category: normalizeValue(hotel.room_category || hotel.room_type || hotel.category || hotel.roomType),
                meal_plan: normalizeValue(hotel.meal_plan || hotel.plan || hotel.mealPlan || hotel.meals),
                inclusions: normalizeValue(hotel.inclusions || hotel.hotel_inclusions || hotel.amenities || hotel.inclusions_text || hotel.perks || hotel.package_inclusions) || (hotel.meal_plan && String(hotel.meal_plan).toLowerCase() !== 'room only' && String(hotel.meal_plan).toLowerCase() !== 'ep' ? hotel.meal_plan : ''),
                // Ensure total_pax is mapped for hotel from guest info if missing
                total_guests: hotel.total_guests || hotel.total_pax || hotel.total_guests_adults || hotel.pax || processed.guest_information?.total_guests_adults || null
            };

            // Fuzzy match location for location_id
            if (mappedHotel.city || mappedHotel.hotel_name) {
                let matchedLocation = mappedHotel.city ? fuzzyMatchLocation(mappedHotel.city, allLocations) : null;

                // If no city match, try hotel name
                if (!matchedLocation && mappedHotel.hotel_name) {
                    matchedLocation = fuzzyMatchLocation(mappedHotel.hotel_name, allLocations);
                    // If matched from hotel name, populate city if missing
                    if (matchedLocation && !mappedHotel.city) {
                        mappedHotel.city = matchedLocation.name;
                    }
                }

                mappedHotel.location_id = matchedLocation?._id || null;
            }
            return mappedHotel;
        });

        // De-dupe Hotels (Name + Check-in)
        const seenHotels = new Set();
        processed.hotel_bookings = processed.hotel_bookings.filter(h => {
            const key = (String(h.hotel_name || '') + String(h.check_in_date || '')).toLowerCase().replace(/\s+/g, '');
            if (key.length < 5) return true;
            if (seenHotels.has(key)) return false;
            seenHotels.add(key);
            return true;
        });
    }

    // 2. Destination Logic: STRICTLY Sync from Hotel Cities ONLY
    processed.tour_info = processed.tour_info || {};

    if (processed.hotel_bookings && Array.isArray(processed.hotel_bookings)) {
        // Group by city to get the earliest check-in for each
        const cityGroups = {};
        processed.hotel_bookings.forEach(hotel => {
            const cityName = hotel.city ? hotel.city.trim() : 'Unknown';
            // Only group if city is valid
            if (cityName && cityName !== 'Unknown') {
                if (!cityGroups[cityName] || (hotel.check_in_date && (!cityGroups[cityName].date || hotel.check_in_date < cityGroups[cityName].date))) {
                    cityGroups[cityName] = {
                        name: cityName,
                        date: hotel.check_in_date || ''
                    };
                }
            }
        });

        const sortedCities = Object.values(cityGroups).sort((a, b) => {
            if (!a.date) return 1;
            if (!b.date) return -1;
            // Attempt to parse date before comparison
            try {
                return new Date(a.date) - new Date(b.date);
            } catch (e) {
                return 0;
            }
        });

        processed.tour_info.destinations = sortedCities.map(cityGroup => {
            const cityName = cityGroup.name;
            const matchedLocation = fuzzyMatchLocation(cityName, allLocations);

            return {
                original_city: cityName,
                city: matchedLocation ? matchedLocation.name : cityName,
                location_id: matchedLocation?._id || null, // Auto-assign ID if matched
                location_name: matchedLocation?.name || cityName,
                journey_date: standardizeDate(cityGroup.date),
                suggested_location: matchedLocation,
                is_verified: !!matchedLocation,
                confidence: matchedLocation ? 'high' : 'none'
            };
        });

        // Ensure at least one destination if hotels exist but no explicit destinations found
        if (processed.tour_info.destinations.length === 0 && sortedCities.length > 0) {
            // Fallback logic already handled above by map, but ensuring non-empty if hotels exist
        }
    } else {
        processed.tour_info.destinations = [];
    }

    // 3. Travel Bookings Mapping & Normalization
    if (processed.travel_bookings && Array.isArray(processed.travel_bookings)) {
        processed.travel_bookings = processed.travel_bookings.map(travel => {
            let mode_of_transport = travel.mode_of_transport ? String(travel.mode_of_transport).toLowerCase() : null;
            let vehicle_number = normalizeValue(travel.vehicle_number || travel.flight_no || travel.train_no || travel.bus_no || travel.flightNo || travel.trainNo);
            let operator = normalizeValue(travel.operator || travel.airline || travel.company || travel.carrier || travel.train_name || travel.trainName);

            // Fix: If mode is train and vehicle_number is empty but operator contains digits/train keywords, move it.
            if (mode_of_transport === 'train') {
                if (!vehicle_number && operator && (/\d+/.test(operator) || /(express|mail|duronto|rajdhani|shatabdi|vande)/i.test(operator))) {
                    vehicle_number = operator;
                    operator = 'IRCTC'; // Default to IRCTC if we moved the name/number
                }
            }

            return {
                ...travel,
                from: normalizeValue(travel.from || travel.departure_station || travel.origin || travel.departureCity),
                to: normalizeValue(travel.to || travel.arrival_station || travel.destination || travel.arrivalCity),
                departure_terminal: sanitizeTerminalNumber(normalizeValue(travel.departure_terminal || travel.from_terminal || travel.dep_terminal || travel.terminal)),
                arrival_terminal: sanitizeTerminalNumber(normalizeValue(travel.arrival_terminal || travel.to_terminal || travel.arr_terminal)),
                journey_date: standardizeDate(travel.journey_date || travel.travel_date || travel.date || travel.journeyDate || travel.travelDate),
                arrival_date: standardizeDate(travel.arrival_date || travel.arr_date || travel.arrivalDate),
                departure_time: standardizeTime(travel.departure_time || travel.departure || travel.depTime),
                arrival_time: standardizeTime(travel.arrival_time || travel.arrival || travel.arrTime),
                pnr: normalizeValue(travel.pnr || travel.pnr_no || travel.pnrNumber),
                travel_class: normalizeValue(travel.travel_class || travel.class_type || travel.class || travel.travelClass),
                vehicle_number: vehicle_number,
                operator: operator,
                mode_of_transport: mode_of_transport,
                // Handle train operator being the train name - ensuring fallback honors the fix above
                train_name: (mode_of_transport === 'train') ? (vehicle_number || operator) : null
            };
        });

        // Group Valid Travel Bookings
        if (processed.travel_bookings.length > 1) {
            const groupedTravels = [];
            const processedIndices = new Set();
            const normalizeStr = (str) => String(str || '').toLowerCase().trim().replace(/\s+/g, ' ');

            const getGroupKey = (item) => {
                const vehicle = normalizeStr(item.vehicle_number || item.pnr);
                if (vehicle.length > 2) {
                    return `VEHICLE:${vehicle}|${normalizeStr(item.journey_date)}|${normalizeStr(item.mode_of_transport)}`;
                }
                return `DETAIL:${normalizeStr(item.mode_of_transport)}|${normalizeStr(item.from)}|${normalizeStr(item.to)}|${normalizeStr(item.journey_date)}|${normalizeStr(item.departure_time)}`;
            };

            processed.travel_bookings.forEach((current, i) => {
                if (processedIndices.has(i)) return;
                const currentKey = getGroupKey(current);
                const sameTripIndices = [i];

                processed.travel_bookings.forEach((other, j) => {
                    if (i !== j && !processedIndices.has(j)) {
                        if (currentKey === getGroupKey(other)) {
                            sameTripIndices.push(j);
                        }
                    }
                });

                if (sameTripIndices.length > 1) {
                    const consolidated = { ...current };
                    const allPnrs = new Set();
                    const allSeats = new Set();
                    const allNames = new Set();

                    sameTripIndices.forEach(idx => {
                        const item = processed.travel_bookings[idx];
                        if (item.pnr) allPnrs.add(item.pnr);
                        if (item.seat_no) String(item.seat_no).split(',').forEach(s => allSeats.add(s.trim()));
                        if (item.passenger_name) String(item.passenger_name).split(',').forEach(n => allNames.add(n.trim()));
                        processedIndices.add(idx);
                    });

                    consolidated.pnr = [...allPnrs].filter(v => v && v !== 'null').join(', ');
                    consolidated.seat_no = [...allSeats].filter(v => v && v !== 'null').join(', ');
                    consolidated.passenger_name = [...allNames].filter(v => v && v !== 'null').join(', ');
                    groupedTravels.push(consolidated);
                } else {
                    groupedTravels.push(current);
                    processedIndices.add(i);
                }
            });
            processed.travel_bookings = groupedTravels;
        }
    }

    // 4. Tour Info Normalization & Inferred Dates
    if (processed.tour_info) {
        const tour = processed.tour_info;
        tour.start_date = standardizeDate(tour.start_date || tour.startDate || tour.tour_start || tour.arrival_date || tour.check_in_date);
        tour.end_date = standardizeDate(tour.end_date || tour.endDate || tour.tour_end || tour.departure_date || tour.check_out_date);
        tour.tour_place = normalizeValue(tour.tour_place || tour.tourPlace || tour.main_destination || tour.city);

        const allDates = [];
        if (processed.hotel_bookings) {
            processed.hotel_bookings.forEach(h => {
                if (h.check_in_date) {
                    const d = new Date(h.check_in_date);
                    if (!isNaN(d.getTime())) allDates.push(d);
                }
                if (h.check_out_date) {
                    const d = new Date(h.check_out_date);
                    if (!isNaN(d.getTime())) allDates.push(d);
                }
            });
        }
        if (processed.travel_bookings) {
            processed.travel_bookings.forEach(t => {
                if (t.journey_date) {
                    const d = new Date(t.journey_date);
                    if (!isNaN(d.getTime())) allDates.push(d);
                }
            });
        }

        const validDates = allDates.filter(d => !isNaN(d.getTime()));
        if (validDates.length > 0) {
            const minDate = new Date(Math.min(...validDates));
            const maxDate = new Date(Math.max(...validDates));

            // Map if null or if inferred represents actual range better
            if (!processed.tour_info.start_date) {
                processed.tour_info.start_date = minDate.toISOString().split('T')[0];
            }
            if (!processed.tour_info.end_date) {
                processed.tour_info.end_date = maxDate.toISOString().split('T')[0];
            }
        }
    }
    return processed;
};

export const getUsername = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return 'Guest';

    // Clean and split by space/comma
    const parts = fullName.trim().replaceAll(',', ' ').split(/\s+/);

    // Common titles to ignore
    const titles = [
        'mr', 'mrs', 'ms', 'miss', 'master', 'dr', 'doctor', 'ca', 'prof', 'rev', 'sir',
        'shri', 'smt', 'shrimati', 'm/s', 'kumari', 'km', 'md'
    ];

    // Find the first part that isn't a title
    for (const part of parts) {
        // Remove common punctuation like periods from the part for comparison
        const cleanPart = part.toLowerCase().replace(/[.]/g, '');
        if (cleanPart && !titles.includes(cleanPart)) {
            // Return exactly as it is but capitalized (preserving potential special chars that are part of the name)
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        }
    }

    return parts[0] || 'Guest';
};

export const mergeVoucherData = (existing, newlyExtracted) => {
    if (!existing) return newlyExtracted;
    if (!newlyExtracted) return existing;

    const merged = { ...existing };

    // 1. Guest Information: Prefer newly extracted if it's not empty, otherwise keep existing
    if (newlyExtracted.guest_information) {
        const newInfo = newlyExtracted.guest_information;
        merged.guest_information = {
            guest_name: newInfo.guest_name || existing.guest_information?.guest_name,
            contact_number: newInfo.contact_number || existing.guest_information?.contact_number,
            total_guests_adults: newInfo.total_guests_adults || existing.guest_information?.total_guests_adults,
            total_guests_children: newInfo.total_guests_children || existing.guest_information?.total_guests_children,
            total_rooms_booked: newInfo.total_rooms_booked || existing.guest_information?.total_rooms_booked,
        };
    }

    // Helper to check if a hotel object contains actual data
    const isPopulatedHotel = (h) => {
        if (!h) return false;
        return Boolean(
            (h.hotel_name && h.hotel_name.trim() !== '') ||
            (h.city && h.city.trim() !== '') ||
            (h.check_in_date && h.check_in_date.trim() !== '') ||
            (h.confirmation_number && h.confirmation_number.trim() !== '')
        );
    };

    // Helper to check if a travel object contains actual data
    const isPopulatedTravel = (t) => {
        if (!t) return false;
        return Boolean(
            (t.from && t.from.trim() !== '') ||
            (t.to && t.to.trim() !== '') ||
            (t.operator && t.operator.trim() !== '') ||
            (t.pnr && t.pnr.trim() !== '') ||
            (t.vehicle_number && t.vehicle_number.trim() !== '') ||
            (t.journey_date && t.journey_date.trim() !== '')
        );
    };

    // 2. Hotel Bookings: Filter out empty placeholder hotels, then merge
    if (newlyExtracted.hotel_bookings?.length > 0) {
        const existingHotels = (existing.hotel_bookings || []).filter(isPopulatedHotel);
        const validNewHotels = newlyExtracted.hotel_bookings.filter(isPopulatedHotel);

        if (existingHotels.length === 0) {
            merged.hotel_bookings = validNewHotels.length > 0 ? validNewHotels : newlyExtracted.hotel_bookings;
        } else {
            existingHotels.forEach(oldH => {
                const matchingNew = validNewHotels.find(newH => 
                    (oldH.hotel_name?.toLowerCase().trim() === newH.hotel_name?.toLowerCase().trim()) ||
                    (oldH.confirmation_number && oldH.confirmation_number === newH.confirmation_number)
                );
                if (matchingNew) {
                    if (!oldH.hotel_phone && matchingNew.hotel_phone) oldH.hotel_phone = matchingNew.hotel_phone;
                    if (!oldH.hotel_address && matchingNew.hotel_address) oldH.hotel_address = matchingNew.hotel_address;
                    if (!oldH.map_link && matchingNew.map_link) oldH.map_link = matchingNew.map_link;
                    if (!oldH.inclusions && matchingNew.inclusions) oldH.inclusions = matchingNew.inclusions;
                    if (!oldH.check_in_time && matchingNew.check_in_time) oldH.check_in_time = matchingNew.check_in_time;
                    if (!oldH.check_out_time && matchingNew.check_out_time) oldH.check_out_time = matchingNew.check_out_time;
                }
            });
            const newHotels = validNewHotels.filter(newH => {
                return !existingHotels.some(oldH =>
                    (oldH.hotel_name?.toLowerCase().trim() === newH.hotel_name?.toLowerCase().trim()) &&
                    (oldH.check_in_date === newH.check_in_date)
                );
            });
            merged.hotel_bookings = [...existingHotels, ...newHotels];
        }
    }

    // 3. Travel Bookings: Filter out empty placeholder travel bookings, then merge
    if (newlyExtracted.travel_bookings?.length > 0) {
        const existingTravels = (existing.travel_bookings || []).filter(isPopulatedTravel);
        const validNewTravels = newlyExtracted.travel_bookings.filter(isPopulatedTravel);

        if (existingTravels.length === 0) {
            merged.travel_bookings = (validNewTravels.length > 0 ? validNewTravels : newlyExtracted.travel_bookings).map(t => ({
                ...t,
                departure_terminal: sanitizeTerminalNumber(t.departure_terminal),
                arrival_terminal: sanitizeTerminalNumber(t.arrival_terminal)
            }));
        } else {
            existingTravels.forEach(oldT => {
                const matchingNew = validNewTravels.find(newT =>
                    (newT.pnr && oldT.pnr === newT.pnr) ||
                    (oldT.operator?.toLowerCase().trim() === newT.operator?.toLowerCase().trim() && oldT.journey_date === newT.journey_date)
                );
                if (matchingNew) {
                    if (!oldT.departure_terminal && matchingNew.departure_terminal) oldT.departure_terminal = sanitizeTerminalNumber(matchingNew.departure_terminal);
                    if (!oldT.arrival_terminal && matchingNew.arrival_terminal) oldT.arrival_terminal = sanitizeTerminalNumber(matchingNew.arrival_terminal);
                    if (!oldT.departure_time && matchingNew.departure_time) oldT.departure_time = matchingNew.departure_time;
                    if (!oldT.arrival_time && matchingNew.arrival_time) oldT.arrival_time = matchingNew.arrival_time;
                    if (!oldT.seat_no && matchingNew.seat_no) oldT.seat_no = matchingNew.seat_no;
                }
            });
            const newTravels = validNewTravels.filter(newT => {
                const hasDuplicate = existingTravels.some(oldT =>
                    (newT.pnr && oldT.pnr === newT.pnr) ||
                    (oldT.operator?.toLowerCase().trim() === newT.operator?.toLowerCase().trim() && oldT.journey_date === newT.journey_date)
                );
                return !hasDuplicate;
            }).map(t => ({
                ...t,
                departure_terminal: sanitizeTerminalNumber(t.departure_terminal),
                arrival_terminal: sanitizeTerminalNumber(t.arrival_terminal)
            }));
            merged.travel_bookings = [...existingTravels, ...newTravels];
        }
    }

    // 4. Tour Info: Update dates if new ones extend the range, merge destinations
    if (newlyExtracted.tour_info) {
        const existingTour = existing.tour_info || {};
        const newTour = newlyExtracted.tour_info;

        merged.tour_info = {
            ...existingTour,
            tour_place: newTour.tour_place || existingTour.tour_place,
            start_date: (newTour.start_date && (!existingTour.start_date || newTour.start_date < existingTour.start_date)) ? newTour.start_date : existingTour.start_date,
            end_date: (newTour.end_date && (!existingTour.end_date || newTour.end_date > existingTour.end_date)) ? newTour.end_date : existingTour.end_date,
        };

        // Merge Destinations
        const existingDests = existingTour.destinations || [];
        const newDests = newTour.destinations || [];
        const filteredNewDests = newDests.filter(newD =>
            !existingDests.some(oldD => oldD.city?.toLowerCase().trim() === newD.city?.toLowerCase().trim())
        );
        merged.tour_info.destinations = [...existingDests, ...filteredNewDests];
    }

    // 5. Itinerary: This is tricky. If we have new itinerary, it might be for different days or a replacement.
    // For now, let's append if it's different days, or just take the longer one?
    // Actually, user said itinerary importer strictly manages itinerary.
    // If newlyExtracted has itinerary, we'll append it if it's for new days.
    if (newlyExtracted.itinerary?.length > 0) {
        const existingItin = existing.itinerary || [];
        const newItin = newlyExtracted.itinerary;

        // Simple merge: append if the day name doesn't exist
        const filteredNewItin = newItin.filter(newDay =>
            !existingItin.some(oldDay => oldDay.day?.toLowerCase().trim() === newDay.day?.toLowerCase().trim())
        );
        merged.itinerary = [...existingItin, ...filteredNewItin];
    }

    // 6. Important Notes: Append unique ones
    if (newlyExtracted.important_notes?.length > 0) {
        const existingNotes = existing.important_notes || [];
        const newNotes = newlyExtracted.important_notes.filter(note => !existingNotes.includes(note));
        merged.important_notes = [...existingNotes, ...newNotes];
    }

    // 7. Contact Details: Append
    if (newlyExtracted.contact_details?.length > 0) {
        merged.contact_details = [...(existing.contact_details || []), ...newlyExtracted.contact_details];
    }

    return merged;
};

export const getDatesInRange = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return [];
    try {
        const startParts = String(startDateStr).split('-');
        const endParts = String(endDateStr).split('-');
        if (startParts.length !== 3 || endParts.length !== 3) {
            const start = new Date(startDateStr);
            const end = new Date(endDateStr);
            if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
            const dates = [];
            const current = new Date(start);
            if (current > end) return [];
            while (current <= end) {
                dates.push(current.toISOString().split('T')[0]);
                current.setDate(current.getDate() + 1);
            }
            return dates;
        }

        const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
        const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));

        if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
        if (start > end) return [];

        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 100) return []; // safety check

        const dates = [];
        const current = new Date(start);
        while (current <= end) {
            const y = current.getFullYear();
            const m = String(current.getMonth() + 1).padStart(2, '0');
            const d = String(current.getDate()).padStart(2, '0');
            dates.push(`${y}-${m}-${d}`);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    } catch (e) {
        console.error("Error getting dates in range:", e);
        return [];
    }
};

